import { SelectColorGroup, SelectColorGroupItem } from '@lastbrand/web-ui/select-color-group'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import React, { Fragment } from 'react'

import { transformImageSrc } from '@src/quince-components/src/atoms/image/image.utils'
import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'

import { colorOptionWrapperClass, redDotClass } from './color-option-ds.styles'
import { optionListGapClass, optionListWrapperClass } from './option-selector-ds.styles'

// Absolute-https a scheme-less swatch URL: `SelectColorGroupItem` uses it as a CSS
// `background-image`, which won't resolve a protocol-relative URL.
export const normalizeSwatchImageUrl = (imgURL?: string | null): string | undefined => {
  if (!imgURL) return undefined
  const src = transformImageSrc(imgURL)
  return src.startsWith('http') ? src : `https:${src}`
}

export interface ColorOptionDSItem {
  optionValue: ProductOptionValue
  /** Whether to show the red sale dot for this swatch (legacy `showRedDot`). */
  showRedDot?: boolean
  /** Stable index used to attach the legacy `data-selector-key` to the first item. */
  index: number
}

export interface ColorGroupDSProps {
  items: ColorOptionDSItem[]
  selectedValue: string | undefined
  onOptionSelect: (value: string) => void
  selectionSetId: string
  /** Forwarded to the per-option wrapper for legacy analytics parity. */
  selectorKey?: string
  /** Group name (e.g. "Color") for the radiogroup aria-label / data-option-type. */
  optionName?: string
  ariaLabel?: string
}

/**
 * COLOR branch: one `SelectColorGroup` Root wraps all color values. The built-in sale
 * dot only renders with a `sale` prop (we don't pass it), so the legacy `showRedDot`
 * dot — kept on its own `areAllColorVariantsOnSale` predicate — is rendered externally.
 * The `+N` overflow is owned by the caller and rendered outside the Root.
 */
export const ColorGroupDS = ({
  items,
  selectedValue,
  onOptionSelect,
  selectionSetId,
  selectorKey,
  optionName,
  ariaLabel,
}: ColorGroupDSProps) => {
  return (
    <div
      data-content-type="option-selector"
      data-option-type={optionName}
      className={cx(optionListWrapperClass, optionListGapClass)}
    >
      <SelectColorGroup
        data-selection-set={selectionSetId}
        // `extended` (32px) matches the PDP/popup swatch; `compact` (24px) is too small here.
        variant="extended"
        // The caller already trims to the visible set, so every item passed must render.
        // `compact` otherwise slices to its default maxVisible (5) and silently drops the rest —
        // pass the full count to disable that trim.
        maxVisible={items.length}
        value={selectedValue}
        onValueChange={onOptionSelect}
        aria-label={ariaLabel}
      >
        {items.map(({ optionValue, showRedDot, index }) => (
          <Fragment key={optionValue.value}>
            <div
              data-content-type="option-wrapper"
              data-value={optionValue.value}
              data-is-selected={optionValue.value === selectedValue}
              data-selector-key={index === 0 ? selectorKey : undefined}
              className={colorOptionWrapperClass}
            >
              <SelectColorGroupItem
                value={optionValue.value}
                hexColor={optionValue.hexCode ? (optionValue.hexCode as `#${string}`) : undefined}
                imageUrl={!optionValue.hexCode ? normalizeSwatchImageUrl(optionValue.imgURL) : undefined}
                aria-label={optionValue.displayName ?? optionValue.value}
                addBorder={optionValue.addBorder ? true : false}
              />
              {showRedDot ? <div className={redDotClass} /> : null}
            </div>
          </Fragment>
        ))}
      </SelectColorGroup>
    </div>
  )
}
